import { ConversationStatus } from 'models/conversations'
import { FC, useMemo, useState } from 'react'

import { ChatBubbleOutline } from '@mui/icons-material'
import { ConversationItem } from './ConversationItem'
import { ConversationListSkeleton } from './ConversationListSkeleton'
import { ConversationSearchBar } from './ConversationSearchBar'
import { ConversationStatusToggle } from './ConversationStatusToggle'
import { EmptyContent } from 'components/common/EmptyContent'
import Stack from '@mui/material/Stack'
import { getConversationDisplayTitle } from '../../helpers/conversationDisplay'
import { lastMessagePreviewText } from '../../helpers/chatAttachments'
import { useChatWidget } from '../../ChatWidget.context'
import { useGetConversations } from 'dataQueries/conversations.query'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

const tabForStatus = (status: ConversationStatus) =>
  status === ConversationStatus.CLOSED ? ConversationStatus.CLOSED : ConversationStatus.ACTIVE

/** The conversation list: status toggle, search, items, empty + loading states. */
export const ConversationList: FC = () => {
  const { t } = useTranslation(['chat'])
  const { baseUserData } = useUserData()
  const { selectedConversationId, selectConversation } = useChatWidget()

  const { data: allData } = useGetConversations()
  const selectedStatus = allData?.content?.find((conversation) => conversation.id === selectedConversationId)?.status
  const selectedTab = selectedStatus && tabForStatus(selectedStatus)

  const [status, setStatus] = useState<ConversationStatus>(selectedTab ?? ConversationStatus.ACTIVE)

  const selectionKey = `${selectedConversationId}:${selectedTab}`
  const [followedSelectionKey, setFollowedSelectionKey] = useState(selectionKey)
  if (followedSelectionKey !== selectionKey) {
    setFollowedSelectionKey(selectionKey)
    if (selectedTab) setStatus(selectedTab)
  }

  const [search, setSearch] = useState('')

  // Platform UUID exposed by /user/me as `internalId` — the same id the backend stamps on
  // `participants[].userId`. Using the Auth0 id here (`auth0|…`) would make every participant
  // comparison miss, causing presence/title fallbacks to misattribute to the caller themselves.
  const currentUserId = baseUserData?.internalId
  const { data, isPending } = useGetConversations(status)
  const conversations = useMemo(() => data?.content ?? [], [data])

  // Always surface the number of ongoing conversations next to the toggle — including when the
  // Closed tab is active — so users can see whether they still have unresolved threads. When the
  // selected tab is already ACTIVE, React Query dedupes by key so this is a single network call.
  const { data: ongoingData } = useGetConversations(ConversationStatus.ACTIVE)
  const ongoingCount = ongoingData?.content?.length ?? 0

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return conversations

    return conversations.filter((conversation) => {
      const title = getConversationDisplayTitle(conversation, currentUserId, t).toLowerCase()
      const preview = lastMessagePreviewText(conversation, t).toLowerCase()
      return title.includes(query) || preview.includes(query)
    })
  }, [conversations, search, currentUserId, t])

  return (
    <Stack height="100%" gap="1.2rem">

      <ConversationStatusToggle
        value={status}
        ongoingCount={ongoingCount > 0 ? ongoingCount : undefined}
        onChange={setStatus}
      />

      <ConversationSearchBar value={search} onChange={setSearch} />

      {isPending && <ConversationListSkeleton />}

      {!isPending && filtered.length === 0 &&
        <EmptyContent
          icon={<ChatBubbleOutline />}
          title={t('chat:empty.title')}
          description={t('chat:empty.description')}
        />
      }

      {!isPending && filtered.length > 0 &&
        <Stack gap="0.4rem" overflow="auto">

          {filtered.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              currentUserId={currentUserId}
              isSelected={conversation.id === selectedConversationId}
              onClick={() => selectConversation(conversation.id)}
            />
          ))}

        </Stack>
      }

    </Stack>
  )
}
